/**
 * OAuth2 authorization-code + PKCE auth against the Salesupply Accounts service
 * (Laravel Passport). Replaces @nuxt-alt/auth.
 *
 * This composable deliberately mirrors the surface the rest of the app already used
 * from @nuxt-alt/auth's `useAuth()` — `loggedIn`, `user`, `loginWith`, `logout`, and
 * `strategy.{token,refreshToken}.{get,set}` / `strategy.options.clientId` — so the token
 * refresh, axios interceptors and route middleware keep working unchanged.
 *
 * Responsibilities split:
 *   - login redirect + PKCE           → @badgateway/oauth2-client (authorize URL + verifier)
 *   - code → token exchange           → manual POST to /platform/authorize (needs the
 *                                       non-standard `response_type` field Passport expects)
 *   - token refresh                   → composables/useTokenRefresh.ts → account-client /oauth/token
 *   - user info                       → GET /me
 */
import { OAuth2Client, generateCodeVerifier } from '@badgateway/oauth2-client';
import Cookies from 'js-cookie';
import { removeCookie } from '~/composables/useCookieManager';

// Session cookies (replace @nuxt-alt/auth's auth._token.social / auth._refresh_token.social).
// The access token is stored WITH its 'Bearer ' prefix to match the previous strategy.token
// contract that the interceptors rely on.
const C_TOKEN = 'su_access_token';
const C_REFRESH = 'su_refresh_token';

// PKCE verifier + state survive the redirect via sessionStorage.
const VERIFIER_KEY = 'oauth2_pkce_verifier';
const STATE_KEY = 'oauth2_state';

export type AuthUser = Record<string, unknown>;

/** Shape of the Laravel Passport token-endpoint response. */
interface PassportTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

export const useAuth = () => {
  const config = useRuntimeConfig().public;
  const accountUrl = config.accountsUrl as string;
  const accountApiUrl = config.accountsApiUrl as string;
  const clientId = config.accountClientId as string;
  const redirectUri = `${config.portalUrl as string}/login`;

  const user = useState<AuthUser | null>('auth-user', () => null);

  const cookieOpts: Cookies.CookieAttributes = {
    domain: (config.cookieDomain as string) || undefined,
    path: '/',
    expires: 30,
    sameSite: 'Lax',
    secure:
      typeof window !== 'undefined' && window.location.protocol === 'https:',
  };

  // Mirrors @nuxt-alt/auth's strategy.token / strategy.refreshToken contract:
  // get() returns the stored string; set(false|null) clears it.
  const token = {
    get: (): string | undefined => Cookies.get(C_TOKEN),
    set: (value: string | false | null | undefined) =>
      value ? Cookies.set(C_TOKEN, value, cookieOpts) : removeCookie(C_TOKEN),
  };
  const refreshToken = {
    get: (): string | undefined => Cookies.get(C_REFRESH),
    set: (value: string | false | null | undefined) =>
      value
        ? Cookies.set(C_REFRESH, String(value), cookieOpts)
        : removeCookie(C_REFRESH),
  };
  const strategy = { token, refreshToken, options: { clientId } };

  /** Step 1 — build the PKCE authorize URL and redirect to the Accounts login. */
  const loginWith = async (): Promise<void> => {
    const oauthClient = new OAuth2Client({
      server: accountUrl,
      clientId,
      authorizationEndpoint: '/platform/login',
    });
    const codeVerifier = await generateCodeVerifier();
    const state = crypto.randomUUID();
    sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(STATE_KEY, state);
    window.location.href = await oauthClient.authorizationCode.getAuthorizeUri({
      redirectUri,
      codeVerifier,
      state,
    });
  };

  /** Load the current user from /me using the stored access token. */
  const fetchUser = async (): Promise<void> => {
    const authz = token.get();
    if (!authz) return;
    const res = await $fetch<Record<string, unknown>>(`${accountApiUrl}/me`, {
      headers: {
        Authorization: authz.startsWith('Bearer') ? authz : `Bearer ${authz}`,
      },
    });
    // Laravel resource endpoints often wrap the payload in `{ data: {...} }`; unwrap when
    // the top level doesn't itself carry the user fields (e.g. `permissions`).
    const wrapped =
      res &&
      typeof res === 'object' &&
      'data' in res &&
      !('permissions' in res);
    user.value = (wrapped ? res.data : res) as AuthUser;
  };

  /**
   * Step 2 — on return to /login?code=…, exchange the code for tokens and load /me.
   *
   * The Accounts token endpoint (/platform/authorize) is NON-STANDARD: it requires a
   * `response_type` field in the exchange (this is what @nuxt-alt/auth sent, and why the
   * old flow worked). We post the exchange manually with the exact fields it expects.
   */
  const handleRedirectCallback = async (): Promise<void> => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const returnedState = url.searchParams.get('state');
    const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
    const expectedState = sessionStorage.getItem(STATE_KEY);

    if (!code) throw new Error('No authorization code in the callback URL');
    if (expectedState && returnedState !== expectedState) {
      throw new Error('State mismatch — aborting (possible CSRF)');
    }
    if (!codeVerifier)
      throw new Error('Missing PKCE verifier — start the login again');

    let res: PassportTokenResponse;
    try {
      res = await $fetch<PassportTokenResponse>(
        `${accountUrl}/platform/authorize`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            response_type: 'code',
            client_id: clientId,
            redirect_uri: redirectUri,
            code,
            code_verifier: codeVerifier,
          }).toString(),
        }
      );
    } catch (e) {
      const data = (
        e as { data?: { error_description?: string; error?: string } }
      ).data;
      throw new Error(data?.error_description || data?.error || String(e), {
        cause: e,
      });
    }

    token.set(`Bearer ${res.access_token}`);
    if (res.refresh_token) refreshToken.set(res.refresh_token);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    await fetchUser();
  };

  const logout = async (): Promise<void> => {
    token.set(false);
    refreshToken.set(false);
    user.value = null;
  };

  return {
    // @nuxt-alt/auth-compatible surface (getters so `auth.loggedIn` / `auth.user` read as values)
    get loggedIn(): boolean {
      return !!token.get();
    },
    get user(): AuthUser | null {
      return user.value;
    },
    strategy,
    loginWith,
    logout,
    // explicit callback + user loading
    handleRedirectCallback,
    fetchUser,
  };
};
